/**
 * Utility functions for formatting questionnaire display names.
 *
 * Display names in the knowledge base use prefixes for sorting:
 * - hx.cv.ar: = history, cardiovascular, arrhythmia
 * - ex.cv: = exam, cardiovascular
 * - eval: = evaluation
 *
 * These are useful for organizing data but ugly for display.
 */

/**
 * Strips prefixes like "hx.cv.ar:", "ex.cv:", "eval:", "fam hx:" from display names.
 *
 * @param displayName - The raw display name with potential prefix
 * @returns The clean display name without prefix
 *
 * @example
 * formatDisplayName("hx.cv.ar: atrial fibrillation") // "atrial fibrillation"
 * formatDisplayName("ex.cv: diastolic murmur, timing") // "diastolic murmur, timing"
 * formatDisplayName("eval: chest pain") // "chest pain"
 * formatDisplayName("fam hx: gallstones") // "gallstones"
 * formatDisplayName("asthma") // "asthma" (no prefix, unchanged)
 */
/**
 * Formats a categorical measurement option for DISPLAY ONLY. The raw string is a stored
 * value, compared by exact match in the clinical engine — `measurement.py` branches on
 * `score == "DetectedAmyloidosis"` — so it must round-trip through the form untouched.
 * Only the label the user reads is rewritten; never the `value` on the MenuItem.
 *
 * A space is inserted solely at a lowercase→uppercase boundary, which leaves acronyms
 * whole. Measured against all 50 distinct option values in measurements.csv: 4 change
 * (the EchoGo amyloidosis and HF classes) and every acronym-bearing value — `AB`, `NA`,
 * `Positive - ATTR-wt`, `Positive - AL` — is returned unchanged (RD 2026-08-08).
 *
 * @example
 * formatOptionLabel("DetectedAmyloidosis")    // "Detected Amyloidosis"
 * formatOptionLabel("NotDetectedHF")          // "Not Detected HF"  (acronym intact)
 * formatOptionLabel("Positive - ATTR-wt")     // "Positive - ATTR-wt" (unchanged)
 * formatOptionLabel("Uncertain")              // "Uncertain" (unchanged)
 */
export function formatOptionLabel(value: unknown): string {
  if (typeof value !== 'string' || !value) return String(value ?? '')
  // Capture-and-reinsert rather than a lookbehind: a regex literal is not transpiled, and
  // lookbehind is unsupported in Safari before 16.4, where it throws at parse time.
  return value.replace(/([a-z])([A-Z])/g, '$1 $2')
}

export function formatDisplayName(displayName: string | undefined | null): string {
  if (!displayName) return ''

  // Pattern matches:
  // - Single word prefix: "eval: "
  // - Dotted prefix: "hx.cv.ar: " or "ex.cv: "
  // - Space-separated prefix: "fam hx: "
  // The prefix must be followed by a colon and optional whitespace
  return displayName.replace(/^[a-z]+([\s.][a-z]+)*:\s*/i, '').trim()
}

/**
 * Strips dotted prefixes like "dx.cv.inv:" from anywhere in the string.
 * Only matches prefixes with at least one dot (e.g., "dx.cv:", "hx.cv.ar:").
 * Does NOT strip single-word prefixes like "Call:" or "Order:" to preserve task titles.
 *
 * @param text - The text containing a potential prefix
 * @returns The text with the dotted prefix removed
 *
 * @example
 * stripPrefixAnywhere("Screen testing sites for dx.cv.inv: internal loop recorder")
 * // "Screen testing sites for internal loop recorder"
 * stripPrefixAnywhere("dx.cv.inv: internal loop recorder") // "internal loop recorder"
 * stripPrefixAnywhere("Call: patient about dx.cv: echo") // "Call: patient about echo"
 */
export function stripPrefixAnywhere(text: string | undefined | null): string {
  if (!text) return ''

  // Pattern matches ONLY dotted prefix patterns like "dx.cv.inv:" (must contain at least one dot)
  // This avoids stripping task action prefixes like "Call:" or "Order:"
  return text.replace(/[a-z]+(\.[a-z]+)+:\s*/gi, '').trim()
}

/**
 * Extracts the prefix from a display name if present.
 * Useful for grouping or categorizing questionnaires.
 *
 * @param displayName - The raw display name with potential prefix
 * @returns The prefix without the colon, or empty string if no prefix
 *
 * @example
 * extractPrefix("hx.cv.ar: atrial fibrillation") // "hx.cv.ar"
 * extractPrefix("eval: chest pain") // "eval"
 * extractPrefix("fam hx: gallstones") // "fam hx"
 * extractPrefix("asthma") // ""
 */
export function extractPrefix(displayName: string | undefined | null): string {
  if (!displayName) return ''

  const match = displayName.match(/^([a-z]+(?:[\s.][a-z]+)*):\s*/i)
  return match ? match[1] : ''
}

/**
 * Parses a prefix into its component parts.
 *
 * @param prefix - The prefix string (e.g., "hx.cv.ar")
 * @returns Array of prefix parts
 *
 * @example
 * parsePrefixParts("hx.cv.ar") // ["hx", "cv", "ar"]
 * parsePrefixParts("eval") // ["eval"]
 */
export function parsePrefixParts(prefix: string): string[] {
  if (!prefix) return []
  return prefix.split('.')
}

/**
 * Maps common prefix codes to human-readable labels.
 */
export const PREFIX_LABELS: Record<string, string> = {
  'hx': 'History',
  'ex': 'Exam',
  'eval': 'Evaluation',
  'cv': 'Cardiovascular',
  'ar': 'Arrhythmia',
  'at': 'Atherosclerosis',
  'bp': 'Blood Pressure',
  'vs': 'Vascular',
  'nv': 'Neurological',
  'ms': 'Musculoskeletal',
  'gi': 'Gastrointestinal',
  'pu': 'Pulmonary',
  'sk': 'Skin',
  'he': 'Hematology',
  'al': 'Allergy/Immunology',
}

/**
 * Converts a prefix to a human-readable category label.
 *
 * @param displayName - The raw display name with potential prefix
 * @returns Human-readable category or empty string
 *
 * @example
 * getPrefixLabel("hx.cv.ar: atrial fibrillation") // "History - Cardiovascular"
 * getPrefixLabel("ex.cv: murmur") // "Exam - Cardiovascular"
 */
export function getPrefixLabel(displayName: string | undefined | null): string {
  const prefix = extractPrefix(displayName)
  if (!prefix) return ''

  const parts = parsePrefixParts(prefix)
  const labels = parts
    .slice(0, 2) // Only use first two parts for brevity
    .map(part => PREFIX_LABELS[part] || part)
    .filter(Boolean)

  return labels.join(' - ')
}

/**
 * Checks if two display names have different prefixes.
 * Useful for determining when to show a separator between items.
 *
 * @param displayName1 - First display name
 * @param displayName2 - Second display name
 * @returns true if prefixes are different (should show separator)
 *
 * @example
 * hasDifferentPrefix("hx.cv.ar: item1", "hx.cv.ar: item2") // false
 * hasDifferentPrefix("hx.cv.ar: item1", "ex.cv: item2") // true
 * hasDifferentPrefix("hx.cv.ar: item1", "hx.cv.bp: item2") // true
 */
export function hasDifferentPrefix(
  displayName1: string | undefined | null,
  displayName2: string | undefined | null
): boolean {
  const prefix1 = extractPrefix(displayName1)
  const prefix2 = extractPrefix(displayName2)
  return prefix1 !== prefix2
}
